import type {
  Adventure,
  AdventureSummary,
  CreateInstanceInput,
  CreateSessionPlanInput,
  Group,
  GroupInput,
  Instance,
  InstancePlayer,
  InstanceState,
  InstanceStatus,
  InstanceSummary,
  Person,
  PersonInput,
  PublicSessionPlan,
  RuleEntry,
  RuleLookupResponse,
  SessionPlan,
  SubmitResponseInput,
  UpdateSessionPlanInput,
} from '@gm-binder/shared';

const BASE = '/api';

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${url}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      /* non-json error body */
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface AudioTrack {
  file: string;
  url: string;
  title: string;
  category?: string;
  loop?: boolean;
  size?: number;
}

export type CompendiumType = 'monsters' | 'spells' | 'equipment' | 'magic-items';

export interface CompendiumEntry {
  index: string;
  name: string;
  url?: string;
}

export interface CompendiumSearchResult {
  type: CompendiumType;
  count: number;
  results: CompendiumEntry[];
}

/** Raw SRD detail object (shape varies per type); consumers narrow as needed. */
export type CompendiumDetail = Record<string, unknown>;

export const api = {
  // Adventures (templates)
  listAdventures: () => request<AdventureSummary[]>('/adventures'),
  getAdventure: (slug: string) => request<Adventure>(`/adventures/${slug}`),
  createAdventure: (data: Partial<Adventure>) =>
    request<Adventure>('/adventures', { method: 'POST', body: JSON.stringify(data) }),
  updateAdventure: (slug: string, data: Partial<Adventure>) =>
    request<Adventure>(`/adventures/${slug}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateSections: (slug: string, sections: unknown[]) =>
    request<unknown[]>(`/adventures/${slug}/sections`, {
      method: 'PUT',
      body: JSON.stringify(sections),
    }),
  updateKinds: (slug: string, kinds: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/adventures/${slug}/kinds`, {
      method: 'PUT',
      body: JSON.stringify(kinds),
    }),
  createEntity: (slug: string, kind: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/adventures/${slug}/entities/${kind}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateEntity: (slug: string, kind: string, id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(
      `/adventures/${slug}/entities/${kind}/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify(data) },
    ),
  deleteEntity: (slug: string, kind: string, id: string) =>
    request<void>(`/adventures/${slug}/entities/${kind}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  deleteAdventure: (slug: string) =>
    request<void>(`/adventures/${slug}`, { method: 'DELETE' }),

  // Instances (saved, resumable runs)
  listInstances: () => request<InstanceSummary[]>('/instances'),
  getInstance: (id: string) => request<Instance>(`/instances/${id}`),
  createInstance: (data: CreateInstanceInput) =>
    request<Instance>('/instances', { method: 'POST', body: JSON.stringify(data) }),
  updateInstance: (id: string, data: Partial<Instance>) =>
    request<Instance>(`/instances/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  patchInstance: (
    id: string,
    data: { name?: string; status?: InstanceStatus; state?: Partial<InstanceState> },
  ) => request<Instance>(`/instances/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteInstance: (id: string) => request<void>(`/instances/${id}`, { method: 'DELETE' }),

  // Player roster + session log on an instance
  setInstancePlayers: (id: string, players: InstancePlayer[]) =>
    request<Instance>(`/instances/${id}/players`, {
      method: 'PUT',
      body: JSON.stringify({ players }),
    }),
  closeInstanceDay: (
    id: string,
    data: {
      notes?: string;
      attendees?: string[];
      durationMin?: number;
      date?: string;
      title?: string;
      keepActive?: boolean;
    },
  ) =>
    request<Instance>(`/instances/${id}/close-day`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // People (GM-global player roster)
  listPeople: () => request<Person[]>('/people'),
  getPerson: (id: string) => request<Person>(`/people/${id}`),
  createPerson: (data: PersonInput) =>
    request<Person>('/people', { method: 'POST', body: JSON.stringify(data) }),
  updatePerson: (id: string, data: Partial<PersonInput>) =>
    request<Person>(`/people/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePerson: (id: string) => request<void>(`/people/${id}`, { method: 'DELETE' }),

  // Groups (reusable parties; the spine of session management)
  listGroups: () => request<Group[]>('/groups'),
  getGroup: (id: string) => request<Group>(`/groups/${id}`),
  createGroup: (data: GroupInput) =>
    request<Group>('/groups', { method: 'POST', body: JSON.stringify(data) }),
  updateGroup: (id: string, data: Partial<GroupInput>) =>
    request<Group>(`/groups/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteGroup: (id: string) => request<void>(`/groups/${id}`, { method: 'DELETE' }),

  // Session scheduling (Calendly-like polls)
  listSessionPlans: (instanceId?: string) =>
    request<SessionPlan[]>(`/session-plans${instanceId ? `?instanceId=${instanceId}` : ''}`),
  getSessionPlan: (planId: string) => request<SessionPlan>(`/session-plans/${planId}`),
  createSessionPlan: (data: CreateSessionPlanInput) =>
    request<SessionPlan>('/session-plans', { method: 'POST', body: JSON.stringify(data) }),
  updateSessionPlan: (planId: string, data: UpdateSessionPlanInput) =>
    request<SessionPlan>(`/session-plans/${planId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteSessionPlan: (planId: string) =>
    request<void>(`/session-plans/${planId}`, { method: 'DELETE' }),

  // Public booking (token-addressed, no auth)
  getPublicPlan: (token: string) =>
    request<PublicSessionPlan>(`/public/session-plans/${token}`),
  submitPlanResponse: (token: string, data: SubmitResponseInput) =>
    request<PublicSessionPlan>(`/public/session-plans/${token}/respond`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // BGM / audio
  listAudio: (slug: string) => request<{ tracks: AudioTrack[] }>(`/audio/${slug}`),
  uploadAudio: async (slug: string, files: File[]): Promise<{ tracks: AudioTrack[] }> => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const res = await fetch(`${BASE}/audio/${slug}`, { method: 'POST', body: form });
    if (!res.ok) throw new ApiError(`Upload failed: ${res.status}`, res.status);
    return res.json();
  },
  deleteAudio: (slug: string, file: string) =>
    request<{ tracks: AudioTrack[] }>(`/audio/${slug}/${encodeURIComponent(file)}`, {
      method: 'DELETE',
    }),

  // Image upload (entity reference images, section maps) → media-relative path
  uploadImage: async (slug: string, file: File): Promise<{ path: string; url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/images/${slug}`, { method: 'POST', body: form });
    if (!res.ok) throw new ApiError(`Upload failed: ${res.status}`, res.status);
    return res.json();
  },

  // Compendium (SRD reference via server-side 5e API proxy + cache)
  searchCompendium: (type: CompendiumType, q: string, limit = 60) =>
    request<CompendiumSearchResult>(
      `/compendium/${type}?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),
  getCompendiumEntry: (type: CompendiumType, index: string) =>
    request<CompendiumDetail>(`/compendium/${type}/${encodeURIComponent(index)}`),

  // Rules assistant (SRD rule-sections + conditions, keyword/synonym lookup)
  lookupRules: (query: string, limit?: number) =>
    request<RuleLookupResponse>('/rules/lookup', {
      method: 'POST',
      body: JSON.stringify({ query, limit }),
    }),
  getRuleEntry: (type: string, index: string) =>
    request<RuleEntry>(`/rules/entry/${encodeURIComponent(type)}/${encodeURIComponent(index)}`),
};

/** Resolve a media-relative path (e.g. "images/x.png") to a served URL. */
export function mediaUrl(slug: string, relativePath?: string): string {
  if (!relativePath) return '';
  if (/^https?:\/\//.test(relativePath) || relativePath.startsWith('/')) return relativePath;
  return `/media/${slug}/${relativePath}`;
}
